// Steps of the guided tutorial (reference tool: Strumenti › Tutorial, TUT). Each step highlights a panel or a card and
// runs a short demo: the animated cursor moves on the fields and fills in an example. The texts (tutorial.steps in the
// translations) are the source tool's, adapted where the platform differs: no "Dati progetto" step, no project file
// save/open (the calculation is saved automatically).
import type { ConnectionType } from "../../../http/screwConnector/formulas";
import type { InputSectionId } from "./types";

// numeric controls the demo types into (HTML ids)
export type TutorialControl =
  | "thetaNum"
  | "b1"
  | "d1"
  | "spessore1"
  | "b2"
  | "d2"
  | "spessore2"
  | "forceFn"
  | "forceFv"
  | "forceFa";

export type TutorialCard = "drawCard" | "solutionsCard" | "posCard" | "reportCard";

// what the cursor points at: a field (HTML control id), the connection-type tile (current or a given type), the 3D
// viewport, or any element marked with data-tut / data-stat
export type TutorialPointer =
  | { control: string }
  | { tile: ConnectionType | "current" }
  | { viewport: true }
  | { css: string };

export type TutorialAction =
  // fills in the example data still empty (TUT_EX), so the demo shows a verified connection
  | { kind: "ensure" }
  | { kind: "point"; at: TutorialPointer }
  | { kind: "type"; control: TutorialControl; value: string }
  // connection type shown for a moment (restored when leaving the step), with an element 1 thickness that suits it
  | { kind: "connection"; type: ConnectionType; t1?: number }
  | { kind: "expandPos" }
  | { kind: "scrollPos" }
  | { kind: "openReport" }
  | { kind: "waitReport" }
  | { kind: "scrollReport" };

export type TutorialStepId =
  | "intro"
  | "connectionType"
  | "element1"
  | "element2"
  | "threeMembers"
  | "panelTimber"
  | "loads"
  | "connection"
  | "grid"
  | "view"
  | "solutions"
  | "positioning"
  | "reportPreview"
  | "reportPdf"
  | "dxf";

export type TutorialStep = {
  id: TutorialStepId; // title, text and optional note: tutorial.steps[id]
  target?: { section: InputSectionId } | { card: TutorialCard } | { css: string };
  // the temporary connection type / expanded position card / open report preview stay for the next step
  keepThree?: boolean;
  keepPos?: boolean;
  keepReport?: boolean;
  demo?: TutorialAction[];
};

// example data (HTML TUT_EX), written only into the fields still empty
export const TUTORIAL_EXAMPLE: Record<TutorialControl, number> = {
  thetaNum: 60,
  b1: 200,
  d1: 400,
  spessore1: 90,
  b2: 300,
  d2: 500,
  spessore2: 90,
  forceFn: 5,
  forceFv: 0,
  forceFa: 0,
};

export const TUTORIAL_STEPS: TutorialStep[] = [
  {
    id: "intro",
  },
  {
    id: "connectionType",
    target: { section: "secTipologia" },
    demo: [
      { kind: "point", at: { tile: "current" } },
      { kind: "type", control: "thetaNum", value: "60" },
    ],
  },
  {
    id: "element1",
    target: { section: "secEl1" },
    demo: [
      { kind: "type", control: "b1", value: "200" },
      { kind: "type", control: "d1", value: "400" },
      { kind: "type", control: "spessore1", value: "90" },
    ],
  },
  {
    id: "element2",
    target: { section: "secEl2" },
    demo: [
      { kind: "type", control: "b2", value: "300" },
      { kind: "type", control: "d2", value: "500" },
      { kind: "type", control: "spessore2", value: "90" },
    ],
  },
  {
    id: "threeMembers",
    target: { section: "secTipologia" },
    keepThree: true,
    demo: [
      { kind: "ensure" },
      { kind: "connection", type: "legno3" },
      { kind: "point", at: { viewport: true } },
      { kind: "connection", type: "legno3d" },
      { kind: "point", at: { viewport: true } },
      { kind: "connection", type: "acciaio3", t1: 8 },
      { kind: "point", at: { viewport: true } },
    ],
  },
  {
    id: "panelTimber",
    target: { section: "secTipologia" },
    keepThree: true,
    demo: [
      { kind: "ensure" },
      { kind: "connection", type: "pannello", t1: 18 },
      { kind: "point", at: { viewport: true } },
      { kind: "connection", type: "pannello3", t1: 18 },
      { kind: "point", at: { viewport: true } },
      { kind: "connection", type: "pannello3d", t1: 18 },
      { kind: "point", at: { viewport: true } },
    ],
  },
  {
    id: "loads",
    target: { section: "secCarichi" },
    demo: [
      { kind: "type", control: "forceFn", value: "5" },
      { kind: "type", control: "forceFv", value: "0" },
      { kind: "type", control: "forceFa", value: "0" },
    ],
  },
  {
    id: "connection",
    target: { section: "secConnessione" },
    demo: [
      { kind: "ensure" },
      { kind: "point", at: { control: "etaFamiglia" } },
      { kind: "point", at: { control: "etaForo" } },
      { kind: "point", at: { control: "etaDiametro" } },
      { kind: "point", at: { control: "etaLunghezza" } },
    ],
  },
  {
    id: "grid",
    target: { section: "secGriglia" },
    demo: [
      { kind: "ensure" },
      { kind: "point", at: { control: "rows" } },
      { kind: "point", at: { control: "cols" } },
    ],
  },
  {
    id: "view",
    target: { card: "drawCard" },
    demo: [
      { kind: "ensure" },
      { kind: "point", at: { viewport: true } },
      { kind: "point", at: { css: '[data-stat="statPill"]' } },
    ],
  },
  {
    id: "solutions",
    target: { card: "solutionsCard" },
    demo: [
      { kind: "ensure" },
      { kind: "point", at: { css: '[data-tut="solPreforo"]' } },
      { kind: "point", at: { css: '[data-tut="findSolutions"]' } },
    ],
  },
  {
    id: "positioning",
    target: { card: "posCard" },
    keepPos: true,
    demo: [{ kind: "ensure" }, { kind: "expandPos" }, { kind: "scrollPos" }],
  },
  {
    id: "reportPreview",
    target: { card: "reportCard" },
    keepReport: true,
    demo: [{ kind: "ensure" }, { kind: "openReport" }, { kind: "waitReport" }, { kind: "scrollReport" }],
  },
  {
    id: "reportPdf",
    target: { css: '[data-tut="navReport"]' },
    demo: [{ kind: "point", at: { css: '[data-tut="navReport"]' } }],
  },
  {
    id: "dxf",
    target: { css: '[data-tut="navDxf"]' },
    demo: [{ kind: "ensure" }, { kind: "point", at: { css: '[data-tut="navDxf"]' } }],
  },
];
